import React, { useEffect, useState } from 'react';
import { Box, Button } from '@cloudscape-design/components';
import useMidway from '../../hooks/useMidway';
import { getAuthUrl, type AuthService } from '../../contstant/apiEndpoints';
import AILoadingScreen from '../AILoadingScreen';

interface AuthHandlerProps {
  children: React.ReactNode;
}

/** Services that mint their own SameSite session cookie after Midway auth. */
type Service = AuthService;

/**
 * Required backing services. Each mints its own SameSite session cookie on the first
 * Midway-authenticated request, and the app cannot function without them, so app
 * readiness gates on all of them.
 */
const REQUIRED_SERVICES: readonly Service[] = ['orbit', 'helios'];

/**
 * Best-effort services warmed up in the background. Starlight (Astra) backs the
 * supplementary copilot feature; a Starlight outage — or its absence in a stage where it
 * is not yet deployed — must never gate app readiness or trigger the auth-failure
 * redirect. Failures are swallowed; the copilot surfaces its own error when opened.
 */
const BEST_EFFORT_SERVICES: readonly Service[] = ['starlight'];

export const AuthHandler: React.FC<AuthHandlerProps> = ({ children }) => {
  const { userAlias, isLoading: midwayLoading, isError: midwayError } = useMidway();
  const [authStatus, setAuthStatus] = useState<'checking' | 'ready' | 'establishing'>(
    'checking',
  );

  useEffect(() => {
    if (userAlias && !midwayLoading && !midwayError) {
      checkAndEstablishSessions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once when auth becomes ready
  }, [userAlias, midwayLoading, midwayError]);

  const checkAndEstablishSessions = async () => {
    setAuthStatus('establishing');

    // Warm up best-effort services (Starlight/Astra) in the background so they never
    // gate readiness or trigger the auth-failure redirect below.
    warmUpBestEffortServices();

    try {
      // Probe each required service; establish a session for any that isn't ready.
      const probes = await Promise.all(
        REQUIRED_SERVICES.map(async (service) => ({
          service,
          ok: await checkServiceAccess(service),
        })),
      );
      const missing = probes.filter((p) => !p.ok).map((p) => p.service);

      if (missing.length > 0) {
        await Promise.all(missing.map(establishSession));
      }
      setAuthStatus('ready');
    } catch (error) {
      console.error('Auth setup failed:', error);
      // Fallback: redirect to establish first session
      window.location.href = getAuthUrl('orbit');
    }
  };

  const warmUpBestEffortServices = (): void => {
    BEST_EFFORT_SERVICES.forEach((service) => {
      void (async () => {
        try {
          if (!(await checkServiceAccess(service))) {
            await establishSession(service);
          }
        } catch {
          // Swallow — a best-effort service must never block the app; the feature that
          // depends on it surfaces its own error when used.
        }
      })();
    });
  };

  const checkServiceAccess = async (service: Service): Promise<boolean> => {
    try {
      const url = getAuthUrl(service);

      const response = await fetch(url, {
        credentials: 'include',
        mode: 'cors',
        cache: 'no-cache',
      });
      return response.ok;
    } catch {
      return false;
    }
  };

  const establishSession = (service: Service): Promise<void> => {
    return new Promise((resolve, reject) => {
      const url = getAuthUrl(service);

      // Try iframe first (silent)
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = url;

      const cleanup = () => {
        if (iframe.parentNode) {
          document.body.removeChild(iframe);
        }
      };

      const timeout = setTimeout(() => {
        cleanup();
        reject(new Error(`${service} session timeout`));
      }, 8000);

      iframe.onload = () => {
        clearTimeout(timeout);
        setTimeout(() => {
          cleanup();
          resolve();
        }, 1000);
      };

      iframe.onerror = () => {
        clearTimeout(timeout);
        cleanup();
        reject(new Error(`${service} session failed`));
      };

      document.body.appendChild(iframe);
    });
  };

  // Loading state with AI-style loading screen
  if (midwayLoading || authStatus !== 'ready') {
    // Check if this is a returning user (has visited before)
    const isReturningUser = localStorage.getItem('gravion_visited') === 'true';

    // Mark as visited for future loads
    if (!isReturningUser) {
      localStorage.setItem('gravion_visited', 'true');
    }

    const firstVisitMessages = [
      'Initializing Gravion AI',
      'Calibrating audit intelligence',
      'Learning your preferences',
      'Preparing smart insights',
      'Ready to assist',
    ];

    const returningUserMessages = [
      'Reconnecting neural pathways',
      'Restoring your session',
      'Syncing latest updates',
      'Back online',
    ];

    const authMessages = ['Verifying identity', 'Securing connection', 'Authenticating'];

    const loadingMessages = midwayLoading
      ? authMessages
      : isReturningUser
        ? returningUserMessages
        : firstVisitMessages;

    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 9999,
        }}
      >
        <AILoadingScreen messages={loadingMessages} />
      </div>
    );
  }

  // Error state
  if (midwayError || !userAlias) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'white',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999,
        }}
      >
        <Box fontSize="heading-s" color="text-status-error" margin={{ bottom: 'm' }}>
          Unable to sign in
        </Box>
        <Button variant="primary" onClick={() => window.location.reload()}>
          Retry
        </Button>
      </div>
    );
  }

  return <>{children}</>;
};
